import clsx from "clsx";
import RadioInput from "../RadioInput/RadioInput";
import { CSSProperties, startTransition, useMemo, useState } from "react";
import useMobile from "src/hooks/useMobile";
import { useSwipeable } from "react-swipeable";
import { ALLOW_SWIPE_IN_DESKTOP } from "src/helpers/constants";

const SWIPE_DELTA = 150;

export type RadioGridType = {
  onChange: (selected: string | number) => void;
  selected: string | number;
  disabled?: boolean;
  options: Array<any>;
  columns: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
};

const RadioGrid: React.FC<RadioGridType> = ({
  disabled = false,
  options,
  columns = 2,
  selected,
  onChange,
  onSwipeLeft,
  onSwipeRight,
}) => {
  const { isMobile } = useMobile();
  const [translateX, setTranslateX] = useState<number>(0);

  const isSwipeable = useMemo(() => {
    return (
      !!onSwipeLeft &&
      !!onSwipeRight &&
      (isMobile ? true : ALLOW_SWIPE_IN_DESKTOP)
    );
  }, [onSwipeLeft, onSwipeRight, isMobile]);

  const handlers = useSwipeable({
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: true,
    onSwiping: ({ deltaX, dir }) => {
      if (dir === "Left" || dir === "Right") {
        startTransition(() => {
          setTranslateX(deltaX);
        });
      }
    },
    onSwiped: (eventData) => {
      const { dir, deltaX } = eventData;
      if (["Left", "Right"].includes(dir)) {
        startTransition(() => {
          if (Math.abs(deltaX) > SWIPE_DELTA) {
            if (dir === "Left") onSwipeLeft?.();
            if (dir === "Right") onSwipeRight?.();
          }
          setTranslateX(0);
        });
      }
    },
  });

  return (
    <div
      className={clsx([
        "grid",
        "gap-2",
        "w-full",
        `grid-cols-${columns}`,
        {
          "pointer-events-none": disabled,
          "cursor-grab": isSwipeable,
        },
      ])}
      {...(isSwipeable ? handlers : {})}
      style={{
        ...((isSwipeable
          ? {
              touchAction: "pan-y",
              transform: `translateX(${translateX}px)`,
              transition: translateX === 0 ? "transform 0.2s ease" : "none",
            }
          : {}) as CSSProperties),
      }}
    >
      {options?.map?.(({ label, description, value }) => {
        return (
          <RadioInput
            label={label}
            description={description}
            selected={value === selected}
            value={value}
            key={value}
            onClick={() => onChange(value)}
          />
        );
      })}
    </div>
  );
};

export default RadioGrid;
